import type { EducationTimeEntry } from '@lms/api-client/backend';

/**
 * Median minutes per course, averaged over locations (the backend returns one
 * median per location), so a portfolio doesn't add up its locations' medians.
 */
export function educationMinutes(entries: readonly EducationTimeEntry[]) {
  const acc = new Map<
    string,
    { curriculumName: string; courseName: string; sum: number; n: number }
  >();
  for (const e of entries) {
    const key = `${e.curriculumName}\u0000${e.courseName}`;
    const cur = acc.get(key) ?? {
      curriculumName: e.curriculumName,
      courseName: e.courseName,
      sum: 0,
      n: 0,
    };
    cur.sum += Math.max(e.count, 0);
    cur.n += 1;
    acc.set(key, cur);
  }
  return [...acc.values()].map(({ curriculumName, courseName, sum, n }) => ({
    curriculumName,
    courseName,
    minutes: Math.round(sum / n / 60),
  }));
}

/**
 * Per curriculum (by name), for a portfolio: each location's curriculum time
 * (the sum of its course medians) averaged over the locations with
 * completions in it. Courses differ per location, so they aren't matched
 * across locations.
 */
export function curriculumMinutesAcrossLocations(
  entries: readonly EducationTimeEntry[],
) {
  // curriculum → location → summed course medians (seconds)
  const totals = new Map<string, Map<string, number>>();
  for (const e of entries) {
    const byLocation = totals.get(e.curriculumName) ?? new Map();
    byLocation.set(
      e.locationName,
      (byLocation.get(e.locationName) ?? 0) + Math.max(e.count, 0),
    );
    totals.set(e.curriculumName, byLocation);
  }
  return [...totals.entries()].map(([curriculumName, byLocation]) => {
    const seconds = [...byLocation.values()];
    return {
      curriculumName,
      minutes: Math.round(
        seconds.reduce((a, b) => a + b, 0) / seconds.length / 60,
      ),
      locations: seconds.length,
    };
  });
}

/** Average over curricula of each curriculum's summed course minutes. */
export function averageCurriculumMinutes(
  minutes: readonly { curriculumName: string; minutes: number }[],
) {
  const perCurriculum = new Map<string, number>();
  for (const m of minutes) {
    perCurriculum.set(
      m.curriculumName,
      (perCurriculum.get(m.curriculumName) ?? 0) + m.minutes,
    );
  }
  const totals = [...perCurriculum.values()];
  return totals.length
    ? Math.round(totals.reduce((a, b) => a + b, 0) / totals.length)
    : 0;
}

/**
 * Total time spent per curriculum (sum over every session, not over the
 * medians), largest first.
 */
export function curriculumTotalSeconds(entries: readonly EducationTimeEntry[]) {
  const totals = new Map<string, number>();
  for (const e of entries) {
    totals.set(
      e.curriculumName,
      (totals.get(e.curriculumName) ?? 0) + Math.max(e.totalSeconds, 0),
    );
  }
  return [...totals.entries()]
    .map(([curriculumName, seconds]) => ({ curriculumName, seconds }))
    .sort((a, b) => b.seconds - a.seconds);
}
